import { useCallback } from "react";
import CoveragePhaseService from "../../../api/services/operation/coverage-phase-service";
import { createNotification } from "../../../common/components";
import { translate } from "../../../common/providers";
import PhaseOperations from "../enum/PhaseOperations";

export function usePhaseOperations({
	isMountedRef,
	projectId,
	setActivePhases,
	setArchivedPhases,
	updateCancelTokenSourceRef,
}) {
	const handleArchive = useCallback(
		(phase, toArchive) => {
			if (!phase?.id) {
				return;
			}
			const operation = toArchive ? PhaseOperations.ARCHIVE : PhaseOperations.UNARCHIVE;
			const payload = {
				...phase,
				operation,
				active: !toArchive,
				archiveDate: toArchive ? new Date().toISOString() : null,
			};
			CoveragePhaseService.updateCoveragePhase(
				{
					projectId,
					phaseId: phase.id,
					phase: payload,
				},
				updateCancelTokenSourceRef.current.token
			)
				.then((updatedPhase) => {
					if (!isMountedRef.current) {
						return;
					}
					const data = updatedPhase || payload;
					if (toArchive) {
						setActivePhases((prev) => prev.filter((p) => p.id !== phase.id));
						setArchivedPhases((prev) =>
							[...prev, data].sort((a, b) => (a.position || 0) - (b.position || 0))
						);
					} else {
						setArchivedPhases((prev) => prev.filter((p) => p.id !== phase.id));
						setActivePhases((prev) =>
							[...prev, data].sort((a, b) => (a.position || 0) - (b.position || 0))
						);
					}
				})
				.catch((err) => console.error("Failed to update archive state:", err));
		},
		[isMountedRef, projectId, setActivePhases, setArchivedPhases, updateCancelTokenSourceRef]
	);

	const handleDelete = useCallback(
		(phaseId) => {
			if (!phaseId) {
				console.error("No phase ID available for deletion");
				return;
			}

			CoveragePhaseService.deleteCoveragePhase(
				{
					projectId,
					phaseId,
				},
				updateCancelTokenSourceRef.current.token
			)
				.then(() => {
					if (!isMountedRef.current) {
						return;
					}
					setActivePhases((prev) => prev.filter((p) => p.id !== phaseId));
					setArchivedPhases((prev) => prev.filter((p) => p.id !== phaseId));
				})
				.catch((err) => {
					console.error("Failed to delete phase:", err);
				});
		},
		[isMountedRef, projectId, setActivePhases, setArchivedPhases, updateCancelTokenSourceRef]
	);

	const handleLock = useCallback(
		(phase, selectedTab) => {
			if (!phase?.id) {
				return;
			}
			const shouldLock = !phase.locked;
			const operation = shouldLock ? PhaseOperations.LOCK : PhaseOperations.UNLOCK;
			const payload = { ...phase, locked: shouldLock, operation };

			const applyLocalUpdate = (updated) => {
				if (selectedTab === 0) {
					setActivePhases((prev) => prev.map((p) => (p.id === phase.id ? { ...p, ...updated } : p)));
				} else {
					setArchivedPhases((prev) => prev.map((p) => (p.id === phase.id ? { ...p, ...updated } : p)));
				}
			};

			applyLocalUpdate(payload);

			CoveragePhaseService.updateCoveragePhase(
				{
					projectId,
					phaseId: phase.id,
					phase: payload,
				},
				updateCancelTokenSourceRef.current.token
			)
				.then((updatedPhase) => {
					if (!isMountedRef.current) {
						return;
					}
					if (updatedPhase) {
						applyLocalUpdate(updatedPhase);
					}
				})
				.catch((err) => {
					console.error("Failed to update lock state:", err);
					if (isMountedRef.current) {
						applyLocalUpdate(phase);
					}
				});
		},
		[isMountedRef, projectId, setActivePhases, setArchivedPhases, updateCancelTokenSourceRef]
	);

	const handleSavePhase = useCallback(
		(editedPhase, selectedTab) => {
			if (!editedPhase.id) {
				console.error("No phase ID available for update");
				return;
			}

			const updateLocalState = () => {
				if (selectedTab === 0) {
					setActivePhases((prev) =>
						prev.map((p) => (p.id === editedPhase.id ? { ...p, ...editedPhase } : p))
					);
				} else {
					setArchivedPhases((prev) =>
						prev.map((p) => (p.id === editedPhase.id ? { ...p, ...editedPhase } : p))
					);
				}
			};
			updateLocalState();

			const updateActivePhases = (updatedPhase) =>
				setActivePhases((prev) => prev.map((p) => (p.id === editedPhase.id ? { ...p, ...updatedPhase } : p)));

			const updateArchivedPhases = (updatedPhase) =>
				setArchivedPhases((prev) => prev.map((p) => (p.id === editedPhase.id ? { ...p, ...updatedPhase } : p)));

			editedPhase.operation = PhaseOperations.UPDATE;
			CoveragePhaseService.updateCoveragePhase(
				{
					projectId,
					phaseId: editedPhase.id,
					phase: editedPhase,
				},
				updateCancelTokenSourceRef.current.token
			)
				.then((updatedPhase) => {
					if (!isMountedRef.current) {
						return;
					}
					if (updatedPhase) {
						if (selectedTab === 0) {
							updateActivePhases(updatedPhase);
						} else {
							updateArchivedPhases(updatedPhase);
						}
					}
				})
				.catch((err) => {
					console.error("Failed to update phase:", err);
				});
		},
		[isMountedRef, projectId, setActivePhases, setArchivedPhases, updateCancelTokenSourceRef]
	);

	const handleSubmitPhaseDialog = useCallback(
		(newPhase, isNew, setOpenPhaseDialog, setTempPhase, setEditingPhaseId) => {
			const apiCall = isNew
				? CoveragePhaseService.createCoveragePhase(
						{ projectId, phase: newPhase },
						updateCancelTokenSourceRef.current.token
				  )
				: CoveragePhaseService.updateCoveragePhase(
						{ projectId, phaseId: newPhase.id, phase: newPhase },
						updateCancelTokenSourceRef.current.token
				  );

			const updatePhasesList = (setPhases, data) => {
				setPhases((prev) => {
					const updatedList = prev.map((p) => {
						if (p.id === data.id) {
							return data;
						}
						return p;
					});
					if (isNew) {
						updatedList.push(data);
					}
					return updatedList.sort((a, b) => (a.position || 0) - (b.position || 0));
				});
			};

			apiCall.then((data) => {
				if (!isMountedRef.current) {
					return;
				}
				if (data) {
					if (data.archived) {
						updatePhasesList(setArchivedPhases, data);
						setActivePhases((prev) => prev.filter((p) => p.id !== data.id));
					} else {
						updatePhasesList(setActivePhases, data);
						setArchivedPhases((prev) => prev.filter((p) => p.id !== data.id));
					}
					if (isNew) {
						createNotification({
							type: "success",
							message: translate("project-phases.create.success"),
						});
					}
				}
				setOpenPhaseDialog(false);
				setTempPhase({});
				setEditingPhaseId(null);
			});
		},
		[isMountedRef, projectId, setActivePhases, setArchivedPhases, updateCancelTokenSourceRef]
	);

	return {
		handleArchive,
		handleDelete,
		handleLock,
		handleSavePhase,
		handleSubmitPhaseDialog,
		updateCancelTokenSourceRef,
	};
}
